import { Component, OnInit, OnDestroy, ViewChild, Input, SimpleChanges, OnChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepicker, MatTimepickerModule, MatTimepickerSelected } from '@angular/material/timepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE, provideNativeDateAdapter } from '@angular/material/core';
import { TicketService } from '../../../../services/ticket.service';
import { ISODayOfWeek, SpecialTicketData, ValidityType } from '../../../../shared/models/ticket';
import { PurchaseFormService } from '../../../../services/purchase-form.service';
import { TranslateModule } from '@ngx-translate/core';
import { DateRange, ValidityDateService } from '../../../../services/validity-date.service';
import { DANISH_TIMEZONE, TimeService } from '../../../../services/time.service';
import { fromZonedTime } from 'date-fns-tz';
import { CustomDateAdapter } from '../../../../services/utility/date-adapter';


@Component({
  selector: 'app-date-card',
  templateUrl: './date-card.component.html',
  styleUrls: ['./date-card.component.css'],
  standalone: true,
  providers: [
    provideNativeDateAdapter(),
    { provide: MAT_DATE_LOCALE, useValue: 'da-DK' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
    {
      provide: MAT_DATE_FORMATS,
      useValue: {
        parse: {
          dateInput: 'dd MMM yyyy',
          timeInput: 'HH:mm',
        },
        display: {
          dateInput: 'dd MMM yyyy',
          monthYearLabel: 'MMM yyyy',
          dateA11yLabel: 'dd MMM yyyy',
          monthYearA11yLabel: 'MMMM yyyy',
          timeInput: 'HH:mm',
          timeOptionLabel: 'HH:mm',
        },
      },
    }
  ],
  imports: [
    FormsModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatInputModule,
    MatFormFieldModule,
    TranslateModule
  ],
})
export class DateCardComponent implements OnInit, OnChanges, OnDestroy {
  @Input() locale: string = 'da-DK';

  startDateObj: Date | null = null;
  minDateObj: Date | null = null;
  maxDateObj: Date | null = null;
  minTimeObj: Date | null = null;
  startTimeObj: Date | null = null;
  startTime: string = '';
  displayTimeRange: string = '';
  validToDate: string = '';
  showTimeInput: boolean = false;
  isTimeFixed: boolean = false;

  private duration: number = 0;
  private fixedEndTime: string = '';
  private destroy$ = new Subject<void>();
  private validityType: ValidityType = ValidityType.None;
  private validWeekdays: number[] = [];
  private shouldDateBePreselected: boolean = false;

  @ViewChild('timepicker') timepicker!: MatTimepicker<any>;

  weekdayFilter = (date: Date | null): boolean => {
    if (!date) return false;
    if (this.validWeekdays.length === 0 && !(this.validityType === ValidityType.SpecificTime && this.fixedEndTime)) return true;

    if (this.validWeekdays.length > 0) {
      const isoDay = date.getDay() === 0 ? 7 : date.getDay();
      if (!this.validWeekdays.includes(isoDay)) return false;
    }

    if (this.validityType === ValidityType.SpecificTime && this.fixedEndTime) {
      if (this.timeService.isToday(date)) {
        const now = this.timeService.nowInDenmark();
        const [endHour, endMinute] = this.fixedEndTime.split(':').map(Number);
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const endMinutes = endHour * 60 + endMinute;
        if (nowMinutes >= endMinutes) return false;
      }
    }

    return true;
  };

  constructor(
    private ticketService: TicketService,
    private formService: PurchaseFormService,
    private dateCalc: ValidityDateService,
    private timeService: TimeService,
    private dateAdapter: DateAdapter<Date>
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locale']) {
      const localeKey = this.locale === 'en-US' ? 'en-GB' : this.locale;
      this.locale = localeKey;
      this.dateAdapter.setLocale(localeKey);
      this.calculateValidTo();
    }
  }

  ngOnInit(): void {
    this.dateAdapter.setLocale(this.locale);
    this.ticketService.ticketData$
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        if (data) {
          this.setupValidationRules(data);
          this.restoreDateState();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }


  // ─── Event Handlers ───────────────────────────────────────────────────────

  onStartDateChange(): void {
    this.updateTimeRestrictions();
    this.calculateValidTo();
  }

  onTimeChange(event: MatTimepickerSelected<Date>): void {
    this.startTimeObj = event.value;
    this.startTime = this.dateCalc.formatTime(event.value);
    this.calculateValidTo();
    setTimeout(() => this.timepicker.close(), 0);
  }

  onTimepickerOpened(): void {
    if (!this.startTimeObj) {
      this.startTimeObj = this.getDefaultStartTime(this.startDateObj);
    }
  }

  // ─── Setup & Configuration ────────────────────────────────────────────────

  private setupValidationRules(data: SpecialTicketData): void {
    const usagePeriod = data.usagePeriods?.[0];
    this.shouldDateBePreselected = data.shouldDateBePreselected ?? false;
    this.validWeekdays = this.resolveValidWeekdays(usagePeriod);
    this.resolveValidityConfig(data.validity);
    this.resolveDateBounds(usagePeriod, this.shouldDateBePreselected);
  }

  private resolveValidWeekdays(usagePeriod: any): number[] {
    const mapped = usagePeriod?.validWeekdays
      ?.map((d: string) => ISODayOfWeek[d as keyof typeof ISODayOfWeek])
      .filter((d: number | undefined): d is number => d !== undefined) ?? [];
    return [...new Set<number>(mapped)];
  }

  private resolveDateBounds(usagePeriod: any, shouldDateBePreselected: boolean): void {
    const today = this.getToday();
    const minDate = usagePeriod?.startDate
      ? this.toMidnight(new Date(usagePeriod.startDate))
      : today;
    this.minDateObj = minDate > today ? minDate : today;
    this.maxDateObj = usagePeriod?.endDate
      ? this.toMidnight(new Date(usagePeriod.endDate))
      : this.getTwoMonthsFromDate(this.minDateObj);

    this.minDateObj = new Date(this.minDateObj);
    while (!this.weekdayFilter(this.minDateObj) && this.minDateObj <= this.maxDateObj) {
      this.minDateObj.setDate(this.minDateObj.getDate() + 1);
    }

    this.startDateObj = shouldDateBePreselected ? this.getPreselectedDate() : null;
    this.startTimeObj = shouldDateBePreselected ? this.getDefaultStartTime(this.startDateObj) : null;
    if (this.isTimeFixed) {
      this.startTime = this.startTime;
    } else if (shouldDateBePreselected && this.startTimeObj) {
      this.startTime = this.dateCalc.formatTime(this.startTimeObj);
    } else {
      this.startTime = '';
    }
  }

  private resolveValidityConfig(validity: SpecialTicketData['validity']): void {
    if (validity?.multipleMinutes) {
      this.applyMinutesConfig(validity.multipleMinutes);
    } else if (validity?.multipleTicketDays) {
      this.applyDaysConfig(ValidityType.TicketDays, validity.multipleTicketDays);
    } else if (validity?.multipleDays) {
      this.applyDaysConfig(ValidityType.CalendarDays, validity.multipleDays);
    } else if (validity?.startTime && validity?.endTime) {
      this.applySpecificTimeConfig(validity.startTime, validity.endTime);
    }
  }

  private applyMinutesConfig(duration: number): void {
    this.validityType = ValidityType.Minutes;
    this.duration = duration;
    this.showTimeInput = true;
    this.isTimeFixed = false;
    this.startTime = '';
    this.displayTimeRange = '';
  }

  private applyDaysConfig(type: ValidityType.TicketDays | ValidityType.CalendarDays, duration: number): void {
    this.validityType = type;
    this.duration = duration;
    this.showTimeInput = false;
    this.displayTimeRange = '';
  }

  private applySpecificTimeConfig(startTime: string, endTime: string): void {
    this.validityType = ValidityType.SpecificTime;
    this.startTime = startTime.substring(0, 5);
    this.fixedEndTime = endTime.substring(0, 5);
    this.displayTimeRange = `${this.startTime} - ${this.fixedEndTime}`;
    this.showTimeInput = true;
    this.isTimeFixed = true;
  }


  // ─── Calculations ─────────────────────────────────────────────────────────

  private calculateValidTo(): void {
    switch (this.validityType) {
      case ValidityType.Minutes: return this.calculateMinutesValidTo();
      case ValidityType.TicketDays: return this.calculateDaysValidTo();
      case ValidityType.CalendarDays: return this.calculateDaysValidTo();
      case ValidityType.SpecificTime: return this.calculateSpecificTimeValidTo();
      default: this.validToDate = '';
    }
  }

  private calculateMinutesValidTo(): void {
    const startDate = this.startDateObj ?? this.timeService.nowInDenmark();

    if (!this.startTimeObj) {
      this.validToDate = '';
      this.displayTimeRange = '';
      return;
    }

    const baseDate = new Date(startDate);
    baseDate.setHours(this.startTimeObj.getHours(), this.startTimeObj.getMinutes(), 0, 0);

    const range = this.dateCalc.calculateRange(baseDate, this.validityType, this.duration, this.startTime, '');
    if (!range) return;

    this.displayTimeRange = `${this.startTime} - ${this.dateCalc.formatTime(range.endDate)}`;

    if (this.startDateObj) {
      this.validToDate = this.dateCalc.formatDatetime(range.endDate, this.locale);
      this.saveFormDateDetails(range);
    }
  }

  private calculateDaysValidTo(): void {
    if (!this.startDateObj) {
      this.validToDate = '';
      this.displayTimeRange = '';
      return;
    }

    let validFromDate = this.startDateObj;
    const isToday = this.timeService.isToday(validFromDate);
    if (isToday) {
      const now = this.timeService.nowInDenmark();
      validFromDate = new Date(this.startDateObj.setHours(now.getHours(), now.getMinutes(), 0, 0));
    }

    const range = this.dateCalc.calculateRange(validFromDate, this.validityType, this.duration, '', '');
    if (!range) return;

    this.validToDate = this.dateCalc.formatDatetime(range.endDate, this.locale);
    this.saveFormDateDetails(range);
  }

  private calculateSpecificTimeValidTo(): void {
    if (!this.startDateObj) {
      this.validToDate = '';
      return;
    }

    const range = this.dateCalc.calculateRange(
      this.startDateObj, this.validityType, this.duration, this.startTime, this.fixedEndTime
    );
    if (!range) return;

    this.validToDate = this.dateCalc.formatDatetime(range.endDate, this.locale);
    this.saveFormDateDetails(range);
  }

  private saveFormDateDetails(range: DateRange): void {
    this.formService.setDateDetails({
      startDate: this.toISOString(range.startDate),
      validToDate: this.toISOString(range.endDate)
    });
  }

  private updateTimeRestrictions(): void {
    if (!this.startDateObj) {
      this.minTimeObj = null;
      this.startTimeObj = null;
      return;
    }

    const isToday = this.timeService.isToday(this.startDateObj);

    if (!isToday) {
      this.minTimeObj = null;
      if (!this.startTimeObj) {
        this.startTimeObj = this.getDefaultStartTime(this.startDateObj);
      }
      return;
    }

    const now = this.timeService.nowInDenmark();
    this.minTimeObj = now;
    const selectedMinutes = this.startTimeObj
      ? this.startTimeObj.getHours() * 60 + this.startTimeObj.getMinutes()
      : null;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const isSelectedTimeValid = selectedMinutes !== null && selectedMinutes >= currentMinutes;

    if (!isSelectedTimeValid) {
      this.startTimeObj = this.startTimeObj !== null ? now : null;
      if (!this.isTimeFixed) {
        this.startTime = this.startTimeObj !== null ? this.dateCalc.formatTime(now) : '';
      }
    }
  }

  private restoreDateState(): void {
    const saved = this.formService.getDateDetails();

    if (!saved?.startDate) {
      this.updateTimeRestrictions();
      this.calculateValidTo();
      return;
    }

    const startDate = new Date(saved.startDate);

    if (isNaN(startDate.getTime()) || startDate < this.getToday()) {
      this.updateTimeRestrictions();
      this.calculateValidTo();
      return;
    }

    this.startDateObj = startDate;

    if (this.validityType === ValidityType.Minutes) {
      this.startTimeObj = startDate;
      this.startTime = this.dateCalc.formatTime(startDate);
    }

    this.updateTimeRestrictions();
    this.calculateValidTo();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  private getPreselectedDate(): Date {
    const today = this.getToday();

    if (!this.minDateObj || this.minDateObj <= today) {
      return today;
    }

    return this.minDateObj;
  }

  private getDefaultStartTime(startDate: Date | null): Date {
    const date = startDate ?? this.timeService.nowInDenmark();
    return this.timeService.isToday(date)
      ? this.timeService.nowInDenmark()
      : this.getNoonOf(date);
  }

  private getTwoMonthsFromDate(fromDate: Date): Date {
    const date = new Date(fromDate);
    date.setMonth(date.getMonth() + 2);
    return this.toMidnight(date);
  }

  private getToday(): Date {
    return this.toMidnight(this.timeService.nowInDenmark());
  }

  private getNoonOf(date: Date): Date {
    const noon = new Date(date);
    noon.setHours(12, 0, 0, 0);
    return noon;
  }

  private toMidnight(date: Date): Date {
    return new Date(date.setHours(0, 0, 0, 0));
  }

  private toISOString(date: Date): string {
    return fromZonedTime(date, DANISH_TIMEZONE).toISOString();
  }
}
